import { useCallback, useEffect, useRef, useState } from 'react';
import { logout, restoreSession } from './api/client.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { DashboardPage } from './features/dashboard/DashboardPage.js';

/**
 * Port of Android's `AppNavGraph`/`AppViewModel`, minus everything that
 * only exists because the APK also runs on the child's phone.
 *
 * The Android app derives its mode from local device state (paired →
 * Child Mode, unpaired → Parent Mode; see docs/ARCHITECTURE.md's "Client
 * app modes"). A browser is never a child device, so there are only two
 * states here: "has a live BFF session" and "doesn't".
 */
type AppState = 'loading' | 'logged-out' | 'authenticated';

export function App() {
  const [appState, setAppState] = useState<AppState>('loading');
  // What the login form prefills its server field with when the browser
  // has nothing remembered — reported by the BFF from `BACKEND_BASE_URL`.
  const [defaultBackendBaseUrl, setDefaultBackendBaseUrl] = useState('');
  // A forced logout can be triggered by several in-flight requests at
  // once (the dashboard polls one per child), so the actual teardown is
  // made idempotent rather than run once per 401.
  const loggingOutRef = useRef(false);

  // Analog of `SessionManager.determineInitialMode` — the session lives
  // in the BFF, so a reload only has to ask whether it is still there.
  useEffect(() => {
    void (async () => {
      const status = await restoreSession().catch(() => null);
      setDefaultBackendBaseUrl(status?.backendBaseUrl ?? '');
      setAppState(status?.authenticated ? 'authenticated' : 'logged-out');
    })();
  }, []);

  /**
   * Both the explicit "Abmelden" button and a forced logout (the BFF
   * answered 401 because the refresh token was rejected) land here. The
   * revoke call is best-effort, exactly like `SessionManager.onLogout`:
   * this tab is logged out either way.
   */
  const handleLoggedOut = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setAppState('logged-out');
    void logout().catch(() => undefined);
  }, []);

  if (appState === 'loading') {
    return (
      <main className="page page--centered">
        <p className="muted" role="status">
          Lädt…
        </p>
      </main>
    );
  }

  if (appState === 'logged-out') {
    return (
      <LoginPage
        defaultBackendBaseUrl={defaultBackendBaseUrl}
        onLoggedIn={() => {
          loggingOutRef.current = false;
          setAppState('authenticated');
        }}
      />
    );
  }

  return <DashboardPage onLoggedOut={handleLoggedOut} />;
}
